import React, { useMemo } from 'react';
import { Avatar } from './Avatar.js';
import { Paragraph } from './Paragraph.js';

function getCardSizeStyles(size) {
    switch (size) {
        case 'small':
            return 'p-3 gap-3';
        case 'large':
            return 'p-6 gap-6 md:p-10 md:gap-10';
        default:
            return 'p-6 gap-6';
    }
}
function getCardVariantStyles(variant, filled) {
    const styles = {
        primary: 'border-green',
        light: 'border-lilac-100',
        medium: 'border-gray-300',
        danger: 'border-red',
        secondary: 'border-lilac-300',
        white: 'border-white',
    };
    switch (variant) {
        case 'primary':
            return filled ? `${styles.primary} bg-green` : styles.primary;
        case 'secondary':
            return filled ? `${styles.secondary} bg-lilac-300` : styles.secondary;
        case 'light':
            return filled ? `border-lilac-50 bg-lilac-50` : styles.light;
        case 'danger':
            return filled ? `border-red-300 bg-red-300` : `${styles.danger} text-red`;
        case 'gradient':
            return filled
                ? `border-none bg-gradient-to-r from-gray-200 to-green-200`
                : 'border-gradient-tr-lilac-green';
        case 'medium':
            return filled ? `border-gray-200 bg-gray-200` : styles.medium;
        case 'white':
            return filled ? `${styles.white} bg-white` : styles.white;
        default:
            return filled ? `border-gray-100 bg-gray-100` : styles.medium;
    }
}
const CardActions = ({ children, className = '', ...props }) => {
    return (React.createElement("div", { className: `grid gap-3 grid-flow-col ${className}`, ...props }, children));
};
const CardContent = ({ children, className = '', ...props }) => {
    return (React.createElement("div", { className: `grid gap-2 ${className}`, ...props }, children));
};
const CardHeader = ({ avatar = {}, className = '', title, size = 'default', subtitle, ...props }) => {
    const isSmall = size === 'small';
    return (React.createElement("header", { className: [
            'grid grid-cols-2-auto items-center',
            isSmall ? 'gap-x-2' : 'gap-x-3',
            className,
        ].join(' '), ...props },
        React.createElement(Avatar, { ...{
                ...avatar,
                className: subtitle ? 'row-span-2' : '',
                size: avatar.size ? avatar.size : isSmall ? 'small' : 'medium',
            } }),
        React.createElement(Paragraph, { size: isSmall ? 'small' : 'medium', className: [
                'font-medium',
                isSmall ? 'leading-none' : 'leading-tight',
            ].join(' ') }, title),
        subtitle && (React.createElement("p", { className: [
                'text-gray-600',
                isSmall ? 'text-xxs leading-none' : 'text-sm',
            ].join(' ') }, subtitle))));
};
const Card = ({ children, className = '', filled = false, rounded = false, size = 'medium', tagName = 'article', variant = 'default', ...props }) => {
    const Component = tagName;
    const variantStyles = useMemo(() => {
        const styles = ['grid gap-3'];
        if (variant !== 'minimal') {
            styles.push('border');
            styles.push(getCardSizeStyles(size));
            styles.push(getCardVariantStyles(variant, filled));
        }
        return styles.join(' ');
    }, [size, variant]);
    return (React.createElement(Component, { className: [rounded ? 'rounded-2xl' : '', variantStyles, className].join(' '), ...props }, children));
};

export { Card, CardActions, CardContent, CardHeader };
//# sourceMappingURL=Card.js.map
